import React, { useCallback, useMemo, useRef } from 'react';
import { SelectOption } from '../Select.types';
import { 
  normalizeValue, 
  isAllSelected, 
  canAddSelection, 
  getEnabledOptionValues,
  removeSpecialValues,
  UI_CONSTANTS 
} from '../utils';

interface UseSelectMultiProps {
  options: SelectOption[];
  value?: string | string[];
  isMulti: boolean;
  showAllOption: boolean;
  maxSelections?: number;
  onChange: (value: string | string[]) => void;
  onAllToggle?: (allSelected: boolean) => void;
}

/**
 * Hook for handling multi-select functionality
 */
export const useSelectMulti = ({
  options,
  value,
  isMulti,
  showAllOption,
  maxSelections,
  onChange,
  onAllToggle,
}: UseSelectMultiProps) => {
  // Normalize value to array format
  const selectedValues = useMemo(() => {
    return normalizeValue(value, isMulti);
  }, [value, isMulti]);

  // Check if "All" is selected
  const allSelected = useMemo(() => {
    return isAllSelected(selectedValues, showAllOption);
  }, [selectedValues, showAllOption]);

  // Registry to store all options seen so far, to persist selected items display
  // even if they are filtered out from the current 'options' prop (e.g. during search)
  const optionsRegistry = useRef<Map<string, SelectOption>>(new Map());

  // Update registry whenever options change
  useMemo(() => {
    options.forEach(opt => {
      optionsRegistry.current.set(opt.value, opt);
    });
  }, [options]);

  // Get selected option objects
  const selectedOptions = useMemo(() => {
    return selectedValues.map(value => {
      // 1. Try to find in current options (most up to date)
      const currentOpt = options.find(opt => opt.value === value);
      if (currentOpt) return currentOpt;

      // 2. Try to find in registry
      const cachedOpt = optionsRegistry.current.get(value);
      if (cachedOpt) return cachedOpt;

      // 3. Fallback for unknown values
      return { 
        value, 
        label: value, 
        disabled: false 
      } as SelectOption;
    });
  }, [options, selectedValues]);

  // Handle individual option selection
  const handleOptionClick = useCallback((optionValue: string) => {
    const option = options.find(opt => opt.value === optionValue);
    if (!option || option.disabled) return;

    if (isMulti) {
      let newValues: string[];
      
      if (allSelected) {
        // If "All" is selected, deselect it and select this specific option
        newValues = [optionValue];
        if (onAllToggle) {
          onAllToggle(false);
        }
      } else {
        // Normal multi-select behavior
        const isCurrentlySelected = selectedValues.includes(optionValue);
        
        if (isCurrentlySelected) {
          // Remove the option
          newValues = removeSpecialValues(selectedValues.filter(v => v !== optionValue));
        } else {
          // Add the option if within limits
          const cleanValues = removeSpecialValues(selectedValues);
          if (canAddSelection(cleanValues, maxSelections)) {
            newValues = [...cleanValues, optionValue];
          } else {
            return; // Don't change if max selections reached
          }
        }
      }

      onChange(newValues);
    } else {
      // Single select
      onChange(optionValue);
    }
  }, [options, isMulti, allSelected, selectedValues, maxSelections, onChange, onAllToggle]);

  // Handle "Select All" functionality
  const handleSelectAll = useCallback(() => {
    if (!isMulti) return;
    
    const allValues = getEnabledOptionValues(options);
    onChange(allValues);
  }, [isMulti, options, onChange]);

  // Handle "Deselect All" functionality
  const handleDeselectAll = useCallback(() => {
    if (!isMulti) return;
    
    onChange([]);
    if (onAllToggle) {
      onAllToggle(false);
    }
  }, [isMulti, onChange, onAllToggle]);

  // Handle "All" toggle (exclusive mode)
  const handleAllToggle = useCallback(() => {
    if (!showAllOption || !isMulti) return;

    const newAllSelected = !allSelected;

    if (newAllSelected) {
      // Select "All" - clear individual selections
      onChange([UI_CONSTANTS.SPECIAL_VALUES.ALL]);
    } else {
      // Deselect "All" - clear all selections
      onChange([]);
    }

    if (onAllToggle) {
      onAllToggle(newAllSelected);
    }
  }, [showAllOption, isMulti, allSelected, onChange, onAllToggle]);

  // Handle tag removal
  const handleRemoveTag = useCallback((valueToRemove: string) => {
    if (!isMulti) return;

    if (valueToRemove === UI_CONSTANTS.SPECIAL_VALUES.ALL) {
      // Remove "All" selection
      onChange([]);
      if (onAllToggle) {
        onAllToggle(false);
      }
    } else {
      // Remove individual selection
      const newValues = removeSpecialValues(selectedValues.filter(v => v !== valueToRemove));
      onChange(newValues);
    }
  }, [isMulti, selectedValues, onChange, onAllToggle]);

  // Handle clear all selections
  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(isMulti ? [] : '');
    if (onAllToggle) {
      onAllToggle(false);
    }
  }, [isMulti, onChange, onAllToggle]);

  return {
    selectedValues,
    selectedOptions,
    allSelected,
    handleOptionClick,
    handleSelectAll,
    handleDeselectAll,
    handleAllToggle,
    handleRemoveTag,
    handleClear,
  };
};
